<main class="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 px-4 py-8">
  <div class="max-w-lg mx-auto">

    <!-- Header -->
    <div class="mb-8 text-center">
      <div class="inline-flex items-center justify-center mb-5">
        <img src="icons/direct-logo-footer.png" alt="Direct Savings & Loans" class="h-14 w-auto"/>
      </div>
      <h1 class="text-2xl font-bold text-white mb-1">Guarantor Declaration</h1>
    </div>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
        <svg class="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
        </svg>
        <p class="text-sm text-gray-500">Loading your form...</p>
      </div>
    }

    <!-- Already Submitted -->
    @else if (isSubmitted) {
      <div class="bg-white rounded-2xl shadow-sm border border-green-100 p-12 text-center">
        <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">Declaration Submitted</h2>
        <p class="text-sm text-gray-500 mb-1">Your guarantor details have been received successfully.</p>
        <p class="text-sm text-gray-400">Thank you for supporting this loan application.</p>
        @if (guarantorInfo?.reference) {
          <div class="mt-5 inline-block bg-gray-50 rounded-xl px-5 py-3">
            <p class="text-xs text-gray-400 mb-1">Loan Reference</p>
            <p class="text-sm font-semibold text-gray-700">{{ guarantorInfo?.reference }}</p>
          </div>
        }
      </div>
    }

    <!-- Form -->
    @else if (!isLoading) {

      <!-- Borrower Info Card -->
      @if (guarantorInfo) {
        <div class="mb-5 bg-white border border-gray-100 rounded-2xl shadow-sm p-4">
          <!-- Avatar + Name row -->
          <div class="flex items-center gap-3 mb-3">
            <div class="flex-shrink-0">
              @if (guarantorInfo.user.userImage) {
                <img [src]="guarantorInfo.user.userImage" alt="Borrower" class="w-14 h-14 rounded-full object-cover border-2 border-primary-100 shadow-sm"/>
              } @else {
                <div class="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center border-2 border-primary-200">
                  <span class="text-xl font-bold text-primary-600">{{ borrowerName.charAt(0).toUpperCase() || '?' }}</span>
                </div>
              }
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-600 font-medium mb-0.5">You are guaranteeing for</p>
              <p class="text-base font-semibold text-gray-900">{{ borrowerName }}</p>
            </div>
          </div>
          <!-- Ref row -->
          <div class="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
            <span class="text-xs text-gray-600 font-medium">Loan Reference</span>
            <span class="text-xs font-semibold text-primary-600">{{ guarantorInfo.reference }}</span>
          </div>
        </div>
      }

      <!-- Step Indicator -->
      <div class="mb-6 relative flex items-start justify-between">
        <!-- Background line -->
        <div class="absolute top-4 left-4 right-4 h-0.5 bg-white/20 -translate-y-1/2"></div>
        <!-- Progress line -->
        <div class="absolute top-4 left-4 h-0.5 bg-white/70 -translate-y-1/2 transition-all duration-300"
          [style.width]="stepNumber === 1 ? '0%' : stepNumber === 2 ? 'calc(50% - 1rem)' : 'calc(100% - 2rem)'"></div>

        @for (step of [
          { key: 'personal', label: 'Info', n: 1 },
          { key: 'documents', label: 'Documents', n: 2 },
          { key: 'declaration', label: 'Declare', n: 3 }
        ]; track step.key) {
          <div class="flex flex-col items-center relative z-10">
            <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-200 border-2"
              [class.bg-white]="stepNumber >= step.n"
              [class.text-primary-700]="stepNumber >= step.n"
              [class.border-white]="stepNumber >= step.n"
              [class.bg-transparent]="stepNumber < step.n"
              [class.text-white]="stepNumber < step.n"
              [class.border-white]="stepNumber < step.n"
              [class.opacity-40]="stepNumber < step.n">
              @if (stepNumber > step.n) {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                </svg>
              } @else {
                {{ step.n }}
              }
            </div>
            <span class="text-xs mt-1.5 font-medium transition-all duration-200"
              [ngClass]="stepNumber >= step.n ? 'text-white' : 'text-white/40'">
              {{ step.label }}
            </span>
          </div>
        }
      </div>

      <!-- STEP 1: Personal Information -->
      @if (currentStep === 'personal') {
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          <h2 class="text-lg font-semibold text-gray-900 mb-1">Personal Information</h2>
          <p class="text-sm text-gray-400 mb-6">Review and confirm your details below. Fields marked <span class="text-error">*</span> are required.</p>

          <form [formGroup]="personalForm" class="space-y-5">

            <!-- Full Name -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Full Name <span class="text-error">*</span>
              </label>
              <input
                formControlName="fullName"
                type="text"
                placeholder="Enter your full name"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'fullName')"
                [class.border-gray-200]="!hasError(personalForm, 'fullName')"
                [class.focus:border-primary-400]="!hasError(personalForm, 'fullName')"
              />
              @if (hasError(personalForm, 'fullName')) {
                <p class="text-xs text-error mt-1.5">Full name is required.</p>
              }
            </div>

            <!-- Ghana Card Number -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Ghana Card Number <span class="text-error">*</span>
              </label>
              <input
                formControlName="ghanaCardNumber"
                type="text"
                placeholder="GHA-XXXXXXXXX-X"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'ghanaCardNumber')"
                [class.border-gray-200]="!hasError(personalForm, 'ghanaCardNumber')"
              />
              @if (hasError(personalForm, 'ghanaCardNumber')) {
                <p class="text-xs text-error mt-1.5">Ghana Card number is required.</p>
              }
            </div>

            <!-- Mobile Number -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Mobile Number <span class="text-error">*</span>
              </label>
              <input
                formControlName="mobileNumber"
                type="tel"
                placeholder="e.g. 0241234567"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'mobileNumber')"
                [class.border-gray-200]="!hasError(personalForm, 'mobileNumber')"
              />
              @if (hasError(personalForm, 'mobileNumber')) {
                <p class="text-xs text-error mt-1.5">Mobile number is required.</p>
              }
            </div>

            <!-- Digital Address -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Digital Address <span class="text-error">*</span>
              </label>
              <input
                formControlName="digitalAddress"
                type="text"
                placeholder="e.g. GA-123-4567"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'digitalAddress')"
                [class.border-gray-200]="!hasError(personalForm, 'digitalAddress')"
              />
              @if (hasError(personalForm, 'digitalAddress')) {
                <p class="text-xs text-error mt-1.5">Enter a valid digital address (e.g. GA-123-4567).</p>
              }
            </div>

            <!-- Place of Work -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Place of Work <span class="text-error">*</span>
              </label>
              <input
                formControlName="placeOfWork"
                type="text"
                placeholder="Enter employer / company name"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'placeOfWork')"
                [class.border-gray-200]="!hasError(personalForm, 'placeOfWork')"
              />
              @if (hasError(personalForm, 'placeOfWork')) {
                <p class="text-xs text-error mt-1.5">Place of work is required.</p>
              }
            </div>

            <!-- Job Detail / Role -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Job Title / Role <span class="text-error">*</span>
              </label>
              <input
                formControlName="jobDetail"
                type="text"
                placeholder="Enter your role or position"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'jobDetail')"
                [class.border-gray-200]="!hasError(personalForm, 'jobDetail')"
              />
              @if (hasError(personalForm, 'jobDetail')) {
                <p class="text-xs text-error mt-1.5">Job title is required.</p>
              }
            </div>

            <!-- Net Salary -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Net Salary (GHS) <span class="text-error">*</span>
              </label>
              <input
                formControlName="netSalary"
                type="number"
                min="1"
                placeholder="e.g. 3500"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none"
                [class.border-red-300]="hasError(personalForm, 'netSalary')"
                [class.border-gray-200]="!hasError(personalForm, 'netSalary')"
              />
              @if (hasError(personalForm, 'netSalary')) {
                <p class="text-xs text-error mt-1.5">Net salary must be greater than 0.</p>
              }
            </div>

            <!-- Relationship to Borrower -->
            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1.5">
                Relationship to Borrower <span class="text-error">*</span>
              </label>
              <select
                formControlName="relationshipToBorrower"
                class="w-full px-4 py-3 rounded-xl border text-sm text-gray-900 transition-all duration-150 outline-none bg-white appearance-none"
                [class.border-red-300]="hasError(personalForm, 'relationshipToBorrower')"
                [class.border-gray-200]="!hasError(personalForm, 'relationshipToBorrower')"
              >
                <option value="" disabled>Select relationship</option>
                @for (opt of relationshipOptions; track opt.value) {
                  <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
              @if (hasError(personalForm, 'relationshipToBorrower')) {
                <p class="text-xs text-error mt-1.5">Relationship is required.</p>
              }
            </div>

          </form>

          <div class="mt-8 flex justify-end">
            <button
              (click)="nextFromPersonal()"
              class="px-7 py-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-all duration-150 shadow-sm flex items-center gap-2"
            >
              Continue
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      }

      <!-- STEP 2: Documents -->
      @if (currentStep === 'documents') {
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 space-y-8">
          <div>
            <h2 class="text-lg font-semibold text-gray-900 mb-1">Document Upload</h2>
            <p class="text-sm text-gray-400">Upload a clear image of your Ghana Card and take a live selfie photo.</p>
          </div>

          <!-- Ghana Card Upload -->
          <div>
            <h3 class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"/>
              </svg>
              Ghana Card Image <span class="text-error">*</span>
            </h3>

            @if (!ghanaCardPreview) {
              <label
                class="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-gray-200 hover:border-primary-400 rounded-2xl cursor-pointer bg-gray-50 hover:bg-primary-50 transition-all duration-200 group"
              >
                <input
                  #ghanaCardInput
                  type="file"
                  accept="image/*"
                  class="hidden"
                  (change)="onGhanaCardSelected($event)"
                />
                <svg class="w-10 h-10 text-gray-300 group-hover:text-primary-400 mb-3 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <p class="text-sm font-medium text-gray-500 group-hover:text-primary-600 transition-colors">Click to upload Ghana Card</p>
                <p class="text-xs text-gray-400 mt-1">JPG, PNG or HEIC — max 5MB</p>
              </label>
            } @else {
              <div class="relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                <img [src]="ghanaCardPreview" alt="Ghana Card" class="w-full object-cover max-h-56"/>
                <button
                  (click)="removeGhanaCard()"
                  class="absolute top-3 right-3 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50 transition-colors"
                >
                  <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
                <div class="px-4 py-3 bg-green-50 border-t border-green-100 flex items-center gap-2">
                  <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <p class="text-xs font-medium text-green-700">{{ ghanaCardFile?.name }}</p>
                </div>
              </div>
            }
          </div>

          <!-- Live Selfie -->
          <div>
            <h3 class="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              Live Selfie Photo <span class="text-error">*</span>
            </h3>

            @if (cameraError) {
              <div class="mb-4 p-3.5 bg-red-50 border border-red-100 rounded-xl">
                <p class="text-xs text-red-600">{{ cameraError }}</p>
              </div>
            }

            @if (!capturedSelfie) {
              <div class="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
                @if (!isCapturing) {
                  <div class="text-center py-10 px-6">
                    <div class="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg class="w-7 h-7 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                    </div>
                    <p class="text-sm font-medium text-gray-600 mb-1">Take a live selfie</p>
                    <p class="text-xs text-gray-400 mb-5">Ensure you are in a well-lit area and facing the camera</p>
                    <button
                      (click)="startCamera()"
                      class="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-all duration-150 shadow-sm inline-flex items-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                      Open Camera
                    </button>
                  </div>
                } @else {
                  <div class="p-4 space-y-4">
                    <div class="relative bg-black rounded-xl overflow-hidden" style="aspect-ratio: 4/3;">
                      <video
                        #videoElement
                        autoplay
                        playsinline
                        class="w-full h-full object-cover"
                        style="transform: scaleX(-1);"
                      ></video>
                      <!-- face guide oval -->
                      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div class="border-4 border-white border-opacity-80 rounded-full" style="width: 55%; aspect-ratio: 3/4; box-shadow: 0 0 0 9999px rgba(0,0,0,0.45);"></div>
                      </div>
                      <p class="absolute bottom-3 left-0 right-0 text-center text-white text-xs font-medium opacity-80">Position your face within the oval</p>
                    </div>
                    <canvas #canvasElement class="hidden"></canvas>
                    <div class="flex gap-3">
                      <button
                        (click)="capturePhoto()"
                        class="flex-1 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-all duration-150 flex items-center justify-center gap-2"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                        Capture Photo
                      </button>
                      <button
                        (click)="stopCamera()"
                        class="px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 transition-all duration-150"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div class="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                <img [src]="capturedSelfie" alt="Selfie" class="w-full object-contain bg-gray-900 max-h-72"/>
                <div class="px-4 py-3 bg-green-50 border-t border-green-100 flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <p class="text-xs font-medium text-green-700">Selfie captured</p>
                  </div>
                  <button
                    (click)="retakePhoto()"
                    class="text-xs text-primary-600 hover:text-primary-700 font-medium underline"
                  >
                    Retake
                  </button>
                </div>
              </div>
            }
          </div>

          <div class="flex justify-between pt-2">
            <button
              (click)="goBack()"
              class="px-6 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-all duration-150 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Back
            </button>
            <button
              (click)="nextFromDocuments()"
              class="px-7 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-all duration-150 shadow-sm flex items-center gap-2"
            >
              Continue
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      }

      <!-- STEP 3: Declaration -->
      @if (currentStep === 'declaration') {
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          <h2 class="text-lg font-semibold text-gray-900 mb-1">Declaration</h2>
          <p class="text-sm text-gray-400 mb-6">Please read the declaration carefully before submitting.</p>

          <!-- Summary -->
          <div class="mb-6 bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100">
            <div class="px-4 py-3 flex justify-between items-center gap-3">
              <span class="text-xs text-gray-500 flex-shrink-0">Full Name</span>
              <span class="text-sm font-medium text-gray-800 text-right truncate">{{ personalForm.value.fullName }}</span>
            </div>
            <div class="px-4 py-3 flex justify-between items-center gap-3">
              <span class="text-xs text-gray-500 flex-shrink-0">Ghana Card No.</span>
              <span class="text-sm font-medium text-gray-800 text-right truncate">{{ personalForm.value.ghanaCardNumber }}</span>
            </div>
            <div class="px-4 py-3 flex justify-between items-center gap-3">
              <span class="text-xs text-gray-500 flex-shrink-0">Relationship</span>
              <span class="text-sm font-medium text-gray-800 capitalize text-right">{{ personalForm.value.relationshipToBorrower }}</span>
            </div>
            <div class="px-4 py-3 flex justify-between items-center gap-4">
              <span class="text-xs text-gray-500 flex-shrink-0">Ghana Card</span>
              <div class="flex items-center gap-2">
                <img [src]="ghanaCardPreview" alt="Ghana Card" class="h-12 w-12 object-cover rounded-full border-2 border-primary-200 shadow-sm"/>
                <span class="text-xs font-medium text-green-600 flex items-center gap-1">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Uploaded
                </span>
              </div>
            </div>
            <div class="px-4 py-3 flex justify-between items-center gap-4">
              <span class="text-xs text-gray-500 flex-shrink-0">Live Photo</span>
              <div class="flex items-center gap-2">
                <img [src]="capturedSelfie" alt="Selfie" class="h-12 w-12 object-cover rounded-full border-2 border-primary-200 shadow-sm"/>
                <span class="text-xs font-medium text-green-600 flex items-center gap-1">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Captured
                </span>
              </div>
            </div>
          </div>

          <!-- Declaration Text -->
          <div class="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6">
            <p class="text-xs text-amber-800 leading-relaxed">
              I, <strong>{{ personalForm.value.fullName || 'the undersigned' }}</strong>, hereby declare that:
            </p>
            <ul class="mt-3 space-y-2 text-xs text-amber-800 leading-relaxed list-disc list-inside">
              <li>I am aware of the loan application made by the borrower referenced above.</li>
              <li>I willingly agree to act as guarantor for this loan application.</li>
              <li>I understand that as guarantor, I may be held liable for repayment if the borrower defaults.</li>
              <li>All information I have provided is true, accurate, and complete to the best of my knowledge.</li>
              <li>I consent to the verification of my identity and the documents submitted herein.</li>
            </ul>
          </div>

          <!-- Declaration Checkbox -->
          <form [formGroup]="declarationForm">
            <label class="flex items-start gap-3 cursor-pointer group">
              <input
                formControlName="declarationAccepted"
                type="checkbox"
                class="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-600 cursor-pointer flex-shrink-0"
              />
              <span class="text-sm text-gray-700 leading-relaxed">
                I have read and understood the declaration above, and I confirm that all submitted information is accurate and truthful.
              </span>
            </label>
            @if (declarationForm.get('declarationAccepted')?.invalid && declarationForm.get('declarationAccepted')?.touched) {
              <p class="text-xs text-error mt-2 ml-7">You must accept the declaration to proceed.</p>
            }
          </form>

          <!-- Signature -->
          <div class="mt-6">
            <div class="flex items-center justify-between mb-3">
              <label class="text-xs font-semibold text-gray-700">
                Signature <span class="text-error">*</span>
              </label>
              @if (signatureData) {
                <button type="button" (click)="clearSignature()"
                  class="text-[11px] font-semibold text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                  Clear
                </button>
              }
            </div>

            <!-- Tab switcher -->
            <div class="flex rounded-lg border border-gray-200 overflow-hidden mb-3 text-xs font-semibold">
              <button type="button"
                (click)="signatureMode = 'draw'; clearSignature()"
                class="flex-1 py-2 flex items-center justify-center gap-1.5 transition-colors"
                [class]="signatureMode === 'draw' ? 'bg-primary-800 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                </svg>
                Draw
              </button>
              <button type="button"
                (click)="signatureMode = 'upload'; clearSignature()"
                class="flex-1 py-2 flex items-center justify-center gap-1.5 transition-colors border-l border-gray-200"
                [class]="signatureMode === 'upload' ? 'bg-primary-800 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                </svg>
                Upload
              </button>
            </div>

            <!-- Draw mode -->
            @if (signatureMode === 'draw') {
              <p class="text-[11px] text-gray-400 mb-2">Draw your signature using your finger or mouse</p>
              <div class="relative rounded-xl overflow-hidden border-2 transition-colors"
                [class]="signatureRequired && !signatureData ? 'border-red-300 bg-red-50/30' : signatureData ? 'border-primary-300 bg-primary-50/20' : 'border-dashed border-gray-300 bg-gray-50'">
                <canvas
                  #signatureCanvas
                  class="w-full h-32 cursor-crosshair touch-none block"
                  (pointerdown)="onSignatureStart($event)"
                  (pointermove)="onSignatureMove($event)"
                  (pointerup)="onSignatureEnd()"
                  (pointerleave)="onSignatureEnd()">
                </canvas>
                @if (!signatureData) {
                  <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-1">
                    <svg class="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                    </svg>
                    <p class="text-xs text-gray-300 font-medium">Sign here</p>
                  </div>
                }
              </div>
            }

            <!-- Upload mode -->
            @if (signatureMode === 'upload') {
              <p class="text-[11px] text-gray-400 mb-2">Upload an image of your handwritten signature (JPG or PNG)</p>
              @if (!signatureData) {
                <label
                  class="flex flex-col items-center justify-center w-full h-32 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
                  [class]="signatureRequired && !signatureData ? 'border-red-300 bg-red-50/30' : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'">
                  <input #signatureUploadInput type="file" accept="image/jpeg,image/png" class="hidden"
                    (change)="onSignatureUploaded($event)"/>
                  <svg class="w-6 h-6 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                  </svg>
                  <p class="text-xs text-gray-400 font-medium">Tap to upload signature</p>
                  <p class="text-[10px] text-gray-300 mt-0.5">JPG or PNG, max 2MB</p>
                </label>
              } @else {
                <div class="relative rounded-xl border-2 border-primary-300 bg-primary-50/20 overflow-hidden h-32 flex items-center justify-center">
                  <img [src]="signatureData" alt="Signature" class="max-h-full max-w-full object-contain p-3"/>
                </div>
              }
            }

            @if (signatureRequired && !signatureData) {
              <p class="text-xs text-error mt-1.5">Please provide your signature before submitting.</p>
            }
          </div>

          <div class="flex gap-3 mt-8">
            <button
              (click)="goBack()"
              class="px-5 py-3 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-all duration-150 flex items-center gap-2 whitespace-nowrap"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Back
            </button>
            <button
              (click)="submitForm()"
              [disabled]="isSubmitting"
              class="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all duration-150 shadow-sm flex items-center justify-center gap-2 whitespace-nowrap"
            >
              @if (isSubmitting) {
                <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Submitting...
              } @else {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                Submit Declaration
              }
            </button>
          </div>
        </div>
      }

    }

    <!-- Footer -->
    <p class="text-center text-xs text-primary-300 mt-8">
      Powered by Direct Savings &amp; Loans
    </p>

  </div>
</main>
